import React, { useState, useRef, useEffect } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { Form, Button, FieldSingleDatePicker, FieldSelect, IconArrowHead, IconSearch, FieldCheckboxGroup } from '../index';
import { pickListingFieldFilters } from '../../containers/SearchPage/SearchPage.shared';
import { convertCategoriesToSelectTreeOptions } from '../../util/search';
import PriceFilter from '../../containers/SearchPage/PriceFilter/PriceFilter';

import css from './IntegratedSearchBar.module.css';
import { priceFilter } from '../../config/configSearch';

const identity = v => v;

// Helper function to process initial values for multi-select fields
const processInitialValues = (initialValues, isBrandMultiSelect, isSportsMultiSelect, isSizeMultiSelect) => {
  if (!initialValues) return {};

  const processed = { ...initialValues };


  // Handle category level parameters (categoryLevel1, categoryLevel2, categoryLevel3)
  if (processed.pub_categoryLevel1 || processed.pub_categoryLevel2 || processed.pub_categoryLevel3) {
    // Find the deepest category level that has a value
    if (processed.pub_categoryLevel3) {
      processed.categoryLevel = processed.pub_categoryLevel3;
    } else if (processed.pub_categoryLevel2) {
      processed.categoryLevel = processed.pub_categoryLevel2;
    } else if (processed.pub_categoryLevel1) {
      processed.categoryLevel = processed.pub_categoryLevel1;
    }
    // Clean up the URL params
    delete processed.pub_categoryLevel1;
    delete processed.pub_categoryLevel2;
    delete processed.pub_categoryLevel3;
  }

  if (isSportsMultiSelect && processed.pub_sportType) {
    const sportsValue = processed.pub_sportType;
    if (typeof sportsValue === 'string') {
      processed.category = sportsValue.split(',').filter(Boolean);
    } else if (Array.isArray(sportsValue)) {
      processed.category = sportsValue;
    }
    delete processed.pub_sportType; // Remove the URL param, keep only the form field
  }

  // If brand is multi-select and we have a pub_brandID value, convert it to array
  if (isBrandMultiSelect && processed.pub_brandID) {
    const brandValue = processed.pub_brandID;
    if (typeof brandValue === 'string') {
      // Convert comma-separated string to array
      processed.brand = brandValue.split(',').filter(Boolean);
    } else if (Array.isArray(brandValue)) {
      processed.brand = brandValue;
    }
    delete processed.pub_brandID; // Remove the URL param, keep only the form field
  }

  // If size is multi-select and we have a pub_sizeid value, convert it to array
  if (isSizeMultiSelect && processed.pub_sizeid) {
    const sizeValue = processed.pub_sizeid;
    if (typeof sizeValue === 'string') {
      // Convert comma-separated string to array
      processed.size = sizeValue.split(',').filter(Boolean);
    } else if (Array.isArray(sizeValue)) {
      processed.size = sizeValue;
    }
    delete processed.pub_sizeid; // Remove the URL param, keep only the form field
  }

  // Handle price parameter - price stays as-is (no conversion needed)
  // Price comes from URL as 'price=10,100' and should be used directly by the form
  if (processed.price) {
    // Price parameter is already in the correct format for the form
    // No conversion needed, just ensure it exists
  }

  return processed;
};

const IntegratedSearchBar = props => {
  const {
    className,
    rootClassName,
    onSubmit,
    initialValues = {},
    searchQuery = '',
    priceRange = { min: 0, max: 1000 }
  } = props;
  
  const intl = useIntl();
  const config = useConfiguration();
  console.log(config);
  const [activeField, setActiveField] = useState(null);

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const formRef = useRef();
  const brandDropdownRef = useRef();
  const categoryDropdownRef = useRef();
  const categoryLevelDropdownRef = useRef();
  const sizeDropdownRef = useRef();
  const priceDropdownRef = useRef();

  // Remove tooltips from all clear buttons
  useEffect(() => {
    const clearButtons = document.querySelectorAll('.clearButton, button[class*="clearButton"]');
    clearButtons.forEach(button => {
      button.removeAttribute('title');
      button.setAttribute('data-no-tooltip', 'true');
    });
  }, [activeField]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutsideBrand = brandDropdownRef.current && !brandDropdownRef.current.contains(event.target);
      const isClickOutsideCategory = categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target);
      const isClickOutsideCategoryLevel = categoryLevelDropdownRef.current && !categoryLevelDropdownRef.current.contains(event.target);
      const isClickOutsideSize = sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target);
      const isClickOutsidePrice = priceDropdownRef.current && !priceDropdownRef.current.contains(event.target);

      if (activeField === 'brand' && isClickOutsideBrand) {
        setActiveField(null);
      } else if (activeField === 'category' && isClickOutsideCategory) {
        setActiveField(null);
      } else if (activeField === 'categoryLevel' && isClickOutsideCategoryLevel) {
        setActiveField(null);
      } else if (activeField === 'size' && isClickOutsideSize) {
        setActiveField(null);
      } else if (activeField === 'price' && isClickOutsidePrice) {
        setActiveField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeField]);
  
  // Get marketplace currency from config
  const marketplaceCurrency = config?.localization?.currency || 'USD';


  // Get category options from configuration (condition filter)
  const getSportsOptions = () => {
    if (!config?.listing?.listingFields) {
      return { 
        options: [{ key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allSports' }) }],
        isMultiSelect: false
      };
    }
    
    try {
      // Get primary filters from listing fields config
      const listingFieldsConfig = pickListingFieldFilters({
        listingFields: config.listing.listingFields,
        locationSearch: '',
        categoryConfiguration: config.categoryConfiguration,
      });

      // Find sport type filter specifically
      const sportTypeFilter = listingFieldsConfig.find(field => 
        field.key === 'sportType' &&
        field.schemaType === 'enum' && 
        field.filterConfig?.group === 'primary' &&
        field.enumOptions?.length > 0
      );

      if (sportTypeFilter) {
        const isMultiSelect = sportTypeFilter.filterConfig?.filterType === 'SelectMultipleFilter';
        const options = isMultiSelect 
          ? sportTypeFilter.enumOptions.map(opt => ({ 
              key: opt.option, 
              label: opt.label,
              filterKey: sportTypeFilter.key,
              scope: sportTypeFilter.scope 
            }))
          : [
              { key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allSports' }) },
              ...sportTypeFilter.enumOptions.map(opt => ({ 
                key: opt.option, 
                label: opt.label,
                filterKey: sportTypeFilter.key,
                scope: sportTypeFilter.scope 
              }))
            ];
            
        return { options, isMultiSelect, sportTypeFilter };
      }
    } catch (error) {
      console.error('Error loading dynamic categories:', error);
    }

    // Fallback to empty options
    return { 
      options: [{ key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allSports' }) }],
      isMultiSelect: false
    };
  };

  // Get brand options from configuration
  const getBrandOptions = () => {
    if (!config?.listing?.listingFields) {
      return { 
        options: [{ key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allBrands' }) }],
        isMultiSelect: false
      };
    }
    
    try {
      // Get primary filters from listing fields config
      const listingFieldsConfig = pickListingFieldFilters({
        listingFields: config.listing.listingFields,
        locationSearch: '',
        categoryConfiguration: config.categoryConfiguration,
      });

      // Find brand filter specifically
      const brandFilter = listingFieldsConfig.find(field => 
        field.key === 'brandID' &&
        field.schemaType === 'enum' && 
        field.filterConfig?.group === 'primary' &&
        field.enumOptions?.length > 0
      );

      if (brandFilter) {
        const isMultiSelect = brandFilter.filterConfig?.filterType === 'SelectMultipleFilter';
        const options = isMultiSelect 
          ? brandFilter.enumOptions.map(opt => ({ 
              key: opt.option, 
              label: opt.label,
              filterKey: brandFilter.key,
              scope: brandFilter.scope 
            }))
          : [
              { key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allBrands' }) },
              ...brandFilter.enumOptions.map(opt => ({ 
                key: opt.option, 
                label: opt.label,
                filterKey: brandFilter.key,
                scope: brandFilter.scope 
              }))
            ];
            
        return { options, isMultiSelect, brandFilter };
      }
    } catch (error) {
      console.error('Error loading brand options:', error);
    }

    // Fallback to empty options
    return { 
      options: [{ key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allBrands' }) }],
      isMultiSelect: false
    };
  };

  // Get category level options from configuration (main categories only)
  const getCategoryLevelOptions = () => {
    if (!config?.categoryConfiguration?.categories) {
      return [];
    }

    try {
      const categories = config.categoryConfiguration.categories;
      // Convert only main categories, excluding subcategories
      return categories.map(category => ({
        option: category.id,
        label: category.name
      }));
    } catch (error) {
      console.error('Error loading category options:', error);
    }

    return [];
  };

  // Get size options from configuration
  const getSizeOptions = () => {
    if (!config?.listing?.listingFields) {
      return { 
        options: [{ key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allSizes' }) }],
        isMultiSelect: false
      };
    }
    
    try {
      const listingFieldsConfig = pickListingFieldFilters({
        listingFields: config.listing.listingFields,
        locationSearch: '',
        categoryConfiguration: config.categoryConfiguration,
      });

      const sizeFilter = listingFieldsConfig.find(field => 
        field.key === 'sizeid' && 
        field.schemaType === 'multi-enum' && 
        field.filterConfig?.group === 'primary' &&
        field.enumOptions?.length > 0
      );

      if (sizeFilter) {
        const isMultiSelect = sizeFilter.filterConfig?.filterType === 'SelectMultipleFilter';
        const options = isMultiSelect 
          ? sizeFilter.enumOptions.map(opt => ({ 
              key: opt.option, 
              label: opt.label,
              filterKey: sizeFilter.key,
              scope: sizeFilter.scope 
            }))
          : [
              { key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allSizes' }) },
              ...sizeFilter.enumOptions.map(opt => ({ 
                key: opt.option, 
                label: opt.label,
                filterKey: sizeFilter.key,
                scope: sizeFilter.scope 
              }))
            ];
            
        return { options, isMultiSelect, sizeFilter };
      }
    } catch (error) {
      console.error('Error loading size options:', error);
    }

    return { 
      options: [{ key: null, label: intl.formatMessage({ id: 'IntegratedSearchBar.allSizes' }) }],
      isMultiSelect: false
    };
  };


  const sportsConfig = getSportsOptions();
  const categoryOptions = sportsConfig.options;
  const isSportsMultiSelect = sportsConfig.isMultiSelect;
  const categoryLevelOptions = getCategoryLevelOptions();
  const brandConfig = getBrandOptions();
  const brandOptions = brandConfig.options;
  const isBrandMultiSelect = brandConfig.isMultiSelect;
  const sizeConfig = getSizeOptions();
  const sizeOptions = sizeConfig.options;
  const isSizeMultiSelect = sizeConfig.isMultiSelect;

  // Process initial values to handle multi-select fields
  const processedInitialValues = processInitialValues(initialValues, isBrandMultiSelect, isSportsMultiSelect, isSizeMultiSelect);

  const classes = classNames(rootClassName || css.root, className);


  const handleSubmit = (values) => {
    const searchParams = {};

    // Handle category level parameter
    if (values.categoryLevel && categoryLevelOptions.length > 0) {
      // Find the category and determine its hierarchy
      const findCategoryHierarchy = (categoryId, categories = config?.categoryConfiguration?.categories || []) => {
        for (const category of categories) {
          if (category.id === categoryId) {
            return [category];
          }
          if (category.subcategories?.length > 0) {
            const subcategoryPath = findCategoryHierarchy(categoryId, category.subcategories);
            if (subcategoryPath.length > 0) {
              return [category, ...subcategoryPath];
            }
          }
        }
        return [];
      };

      const hierarchy = findCategoryHierarchy(values.categoryLevel);
      hierarchy.forEach((category, index) => {
        const levelParam = `pub_categoryLevel${index + 1}`;
        searchParams[levelParam] = category.id;
      });
    }
    
    // Handle dynamic category parameter
    if (isSportsMultiSelect && values.category && Array.isArray(values.category) && values.category.length > 0) {
      // Multi-select sports handling
      if (sportsConfig.sportTypeFilter) {
        const paramName = `pub_${sportsConfig.sportTypeFilter.key}`;
        searchParams[paramName] = values.category.join(',');
      }
    } else if (!isSportsMultiSelect && values.category && categoryOptions.length > 1) {
      // Single-select sports handling
      const selectedOption = categoryOptions.find(opt => opt.key === values.category);
      if (selectedOption && selectedOption.filterKey && selectedOption.scope) {
        const paramName = `pub_${selectedOption.filterKey}`;
        searchParams[paramName] = values.category;
      }
    }
    
    // Handle dynamic brand parameter
    if (isBrandMultiSelect && values.brand && Array.isArray(values.brand) && values.brand.length > 0) {
      // Multi-select brand handling
      if (brandConfig.brandFilter) {
        const paramName = `pub_${brandConfig.brandFilter.key}`;
        searchParams[paramName] = values.brand.join(',');
      }
    } else if (!isBrandMultiSelect && values.brand && brandOptions.length > 1) {
      // Single-select brand handling
      const selectedBrand = brandOptions.find(opt => opt.key === values.brand);
      if (selectedBrand && selectedBrand.filterKey && selectedBrand.scope) {
        const paramName = `pub_${selectedBrand.filterKey}`;
        searchParams[paramName] = values.brand;
      }
    }

    // Handle dynamic size parameter
    if (isSizeMultiSelect && values.size && Array.isArray(values.size) && values.size.length > 0) {
      // Multi-select size handling
      if (sizeConfig.sizeFilter) {
        const paramName = `pub_${sizeConfig.sizeFilter.key}`;
        searchParams[paramName] = values.size.join(',');
      }
    } else if (!isSizeMultiSelect && values.size && sizeOptions.length > 1) {
      // Single-select size handling
      const selectedSize = sizeOptions.find(opt => opt.key === values.size);
      if (selectedSize && selectedSize.filterKey && selectedSize.scope) {
        const paramName = `pub_${selectedSize.filterKey}`;
        searchParams[paramName] = values.size;
      }
    }

    // Handle price parameter
    if (values.price) {
      searchParams.price = values.price;
    }

    onSubmit(searchParams);
  };

  return (
    <div className={classes}>
      <FinalForm
        initialValues={processedInitialValues}
        onSubmit={handleSubmit}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, values, form }) => {
          formRef.current = form;
          
          // Helper to check if multi-select fields have selections
          const hasSelections = (fieldName, isMultiSelect) => {
            if (!isMultiSelect) return false;
            const value = values[fieldName];
            return Array.isArray(value) && value.length > 0;
          };

          // Helper to check if single-select fields have selections
          const hasSingleSelection = (fieldName, options = []) => {
            const value = values[fieldName];

            // Simple handling for categoryLevel which now contains only main categories
            if (fieldName === 'categoryLevel' && value && value !== '') {
              return options.some(opt => opt.option === value);
            }

            // Regular check for other fields
            return value && value !== '' && options.some(opt => opt.key === value);
          };

          // Helper to check if any field has selection (for consistent styling)
          const hasFieldSelection = (fieldName, isMultiSelect, options = []) => {
            if (isMultiSelect) {
              return hasSelections(fieldName, isMultiSelect);
            } else {
              return hasSingleSelection(fieldName, options);
            }
          };
          
          // Helper to clear field selections
          const clearField = (fieldName) => {
            form.change(fieldName, []);
          };
          
          return (
          <Form className={css.form} onSubmit={handleSubmit}>
            <div className={css.searchContainer}>

              {/* Category Level Filter */}
              <div
                ref={categoryLevelDropdownRef}
                className={classNames(css.searchField, {
                  [css.active]: activeField === 'categoryLevel',
                  [css.hasSelections]: hasFieldSelection('categoryLevel', false, categoryLevelOptions)
                })}
                onClick={() => setActiveField('categoryLevel')}
                style={{ position: 'relative' }}
              >
                <div className={classNames(css.fieldContent, {
                  [css.hasClearButton]: hasFieldSelection('categoryLevel', false, categoryLevelOptions)
                })}>
                  <Field
                    name="categoryLevel"
                    render={({ input }) => {
                      const selectedOption = categoryLevelOptions.find(opt => opt.key === input.value);
                      const displayText = selectedOption ? selectedOption.label : intl.formatMessage({ id: 'IntegratedSearchBar.categoryLabel' });

                      return (
                        <div
                          className={classNames(css.fieldInput, { [css.hasSelection]: !!selectedOption && selectedOption.key })}
                          title={displayText}
                          data-tooltip={displayText}
                        >
                          {displayText}
                        </div>
                      );
                    }}
                  />
                </div>

                {/* Clear button for category level */}
                {hasFieldSelection('categoryLevel', false, categoryLevelOptions) && (
                  <button
                    type="button"
                    className={css.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      form.change('categoryLevel', '');
                    }}
                  />
                )}

                {/* Category Level Dropdown */}
                {activeField === 'categoryLevel' && (
                  <div
                    className={css.brandFilterDropdown}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Field
                      name="categoryLevel"
                      render={({ input, meta }) => {
                        // Use categories directly (main categories only)
                        const flatOptions = categoryLevelOptions.map(category => ({
                          key: category.option,
                          label: category.label,
                          level: 1
                        }));

                        return (
                          <div>
                            <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
                              <FormattedMessage id="IntegratedSearchBar.categoryLabel" />
                            </div>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {flatOptions.map(option => (
                                <div
                                  key={option.key}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    margin: '2px 0',
                                    backgroundColor: input.value === option.key ? '#e6f3ff' : 'transparent',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#333'
                                  }}
                                  onClick={() => {
                                    input.onChange(option.key);
                                    setActiveField(null);
                                  }}
                                  onMouseEnter={(e) => {
                                    if (input.value !== option.key) {
                                      e.target.style.backgroundColor = '#f8f9ff';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (input.value !== option.key) {
                                      e.target.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  {option.label}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Sport Filter - Hidden on mobile */}
              {!isMobile && (
              <div
                className={classNames(css.searchField, {
                  [css.active]: activeField === 'category',
                  [css.hasSelections]: hasFieldSelection('category', isSportsMultiSelect, categoryOptions)
                })}
                onClick={() => setActiveField('category')}
                style={{ position: 'relative' }}
              >
                <div className={classNames(css.fieldContent, {
                  [css.hasClearButton]: hasFieldSelection('category', isSportsMultiSelect, categoryOptions)
                })}>
                  {isSportsMultiSelect ? (
                    <div
                      className={classNames(css.fieldInput, { [css.hasSelection]: hasFieldSelection('category', isSportsMultiSelect, categoryOptions) })}
                      title={(() => {
                        if (hasFieldSelection('category', isSportsMultiSelect, categoryOptions) && values.category && Array.isArray(values.category)) {
                          const selectedLabels = values.category.map(val => {
                            const option = categoryOptions.find(opt => opt.key === val);
                            return option ? option.label : val;
                          });
                          return selectedLabels.map(label => `• ${label}`).join('\n');
                        }
                        return intl.formatMessage({ id: 'IntegratedSearchBar.sportLabel' });
                      })()}
                      data-tooltip={(() => {
                        if (hasFieldSelection('category', isSportsMultiSelect, categoryOptions) && values.category && Array.isArray(values.category)) {
                          const selectedLabels = values.category.map(val => {
                            const option = categoryOptions.find(opt => opt.key === val);
                            return option ? option.label : val;
                          });
                          return selectedLabels.map(label => `• ${label}`).join('\n');
                        }
                        return intl.formatMessage({ id: 'IntegratedSearchBar.sportLabel' });
                      })()}
                    >
                      <FormattedMessage id="IntegratedSearchBar.sportLabel" />
                    </div>
                  ) : (
                    <Field
                      name="category"
                      render={({ input, meta }) => {
                        const selectedOption = categoryOptions.find(opt => opt.key === input.value);
                        const displayText = selectedOption ? selectedOption.label : intl.formatMessage({ id: 'IntegratedSearchBar.sportLabel' });

                        return (
                          <select
                            {...input}
                            className={css.selectInput}
                            title={displayText}
                            data-tooltip={displayText}
                            onFocus={() => setActiveField('category')}
                            onBlur={() => setActiveField(null)}
                          >
                            <option value="">{intl.formatMessage({ id: 'IntegratedSearchBar.sportLabel' })}</option>
                            {categoryOptions.slice(1).map(option => (
                              <option key={option.key} value={option.key}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        );
                      }}
                    />
                  )}
                </div>

                {/* Clear button for sports */}
                {hasFieldSelection('category', isSportsMultiSelect, categoryOptions) && (
                  <button
                    type="button"
                    className={css.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearField('category');
                    }}
                  />
                )}

                {/* Sports Multi-Select Filter (appears when category field is active and is multi-select) */}
                {activeField === 'category' && isSportsMultiSelect && (
                  <div
                    ref={categoryDropdownRef}
                    className={css.brandFilterDropdown}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FieldCheckboxGroup
                      name="category"
                      id="IntegratedSearchBar.categoryFilter"
                      label={intl.formatMessage({ id: 'IntegratedSearchBar.sportLabel' })}
                      options={categoryOptions}
                      twoColumns={false}
                    />
                  </div>
                )}
              </div>
              )}

              {/* Brand Filter - Hidden on mobile */}
              {!isMobile && (
              <div 
                className={classNames(css.searchField, {
                  [css.active]: activeField === 'brand',
                  [css.hasSelections]: hasFieldSelection('brand', isBrandMultiSelect, brandOptions)
                })}
                onClick={() => setActiveField('brand')}
                style={{ position: 'relative' }}
              >
                <div className={classNames(css.fieldContent, {
                  [css.hasClearButton]: hasFieldSelection('brand', isBrandMultiSelect, brandOptions)
                })}>
                  {isBrandMultiSelect ? (
                    <div
                      className={classNames(css.fieldInput, { [css.hasSelection]: hasFieldSelection('brand', isBrandMultiSelect, brandOptions) })}
                      title={(() => {
                        if (hasFieldSelection('brand', isBrandMultiSelect, brandOptions) && values.brand && Array.isArray(values.brand)) {
                          const selectedLabels = values.brand.map(val => {
                            const option = brandOptions.find(opt => opt.key === val);
                            return option ? option.label : val;
                          });
                          return selectedLabels.map(label => `• ${label}`).join('\n');
                        }
                        return intl.formatMessage({ id: 'IntegratedSearchBar.brandLabel' });
                      })()}
                      data-tooltip={(() => {
                        if (hasFieldSelection('brand', isBrandMultiSelect, brandOptions) && values.brand && Array.isArray(values.brand)) {
                          const selectedLabels = values.brand.map(val => {
                            const option = brandOptions.find(opt => opt.key === val);
                            return option ? option.label : val;
                          });
                          return selectedLabels.map(label => `• ${label}`).join('\n');
                        }
                        return intl.formatMessage({ id: 'IntegratedSearchBar.brandLabel' });
                      })()}
                    >
                      <FormattedMessage id="IntegratedSearchBar.brandLabel" />
                    </div>
                  ) : (
                    <Field
                      name="brand"
                      render={({ input, meta }) => {
                        const selectedOption = brandOptions.find(opt => opt.key === input.value);
                        const displayText = selectedOption ? selectedOption.label : intl.formatMessage({ id: 'IntegratedSearchBar.brandLabel' });

                        return (
                          <select
                            {...input}
                            className={css.selectInput}
                            title={displayText}
                            data-tooltip={displayText}
                            onFocus={() => setActiveField('brand')}
                            onBlur={() => setActiveField(null)}
                          >
                            <option value="">{intl.formatMessage({ id: 'IntegratedSearchBar.brandLabel' })}</option>
                            {brandOptions.slice(1).map(option => (
                              <option key={option.key} value={option.key}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        );
                      }}
                    />
                  )}
                </div>
                
                {/* Clear button for brand */}
                {hasFieldSelection('brand', isBrandMultiSelect, brandOptions) && (
                  <button
                    type="button"
                    className={css.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearField('brand');
                    }}
                  />
                )}
                
                {/* Brand Multi-Select Filter (appears when brand field is active and is multi-select) */}
                {activeField === 'brand' && isBrandMultiSelect && (
                  <div 
                    ref={brandDropdownRef}
                    className={css.brandFilterDropdown}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FieldCheckboxGroup
                      name="brand"
                      id="IntegratedSearchBar.brandFilter"
                      label={intl.formatMessage({ id: 'IntegratedSearchBar.brandLabel' })}
                      options={brandOptions}
                      twoColumns={false}
                    />
                  </div>
                )}
              </div>
              )}

              {/* Size Filter - Hidden on mobile */}
              {!isMobile && (
              <div 
                className={classNames(css.searchField, {
                  [css.active]: activeField === 'size',
                  [css.hasSelections]: hasFieldSelection('size', isSizeMultiSelect, sizeOptions)
                })}
                onClick={() => setActiveField('size')}
                style={{ position: 'relative' }}
              >
                <div className={classNames(css.fieldContent, {
                  [css.hasClearButton]: hasFieldSelection('size', isSizeMultiSelect, sizeOptions)
                })}>
                  {isSizeMultiSelect ? (
                    <div
                      className={classNames(css.fieldInput, { [css.hasSelection]: hasFieldSelection('size', isSizeMultiSelect, sizeOptions) })}
                      title={(() => {
                        if (hasFieldSelection('size', isSizeMultiSelect, sizeOptions) && values.size && Array.isArray(values.size)) {
                          const selectedLabels = values.size.map(val => {
                            const option = sizeOptions.find(opt => opt.key === val);
                            return option ? option.label : val;
                          });
                          return selectedLabels.map(label => `• ${label}`).join('\n');
                        }
                        return intl.formatMessage({ id: 'IntegratedSearchBar.sizeLabel' });
                      })()}
                      data-tooltip={(() => {
                        if (hasFieldSelection('size', isSizeMultiSelect, sizeOptions) && values.size && Array.isArray(values.size)) {
                          const selectedLabels = values.size.map(val => {
                            const option = sizeOptions.find(opt => opt.key === val);
                            return option ? option.label : val;
                          });
                          return selectedLabels.map(label => `• ${label}`).join('\n');
                        }
                        return intl.formatMessage({ id: 'IntegratedSearchBar.sizeLabel' });
                      })()}
                    >
                      <FormattedMessage id="IntegratedSearchBar.sizeLabel" />
                    </div>
                  ) : (
                    <Field
                      name="size"
                      render={({ input, meta }) => {
                        const selectedOption = sizeOptions.find(opt => opt.key === input.value);
                        const displayText = selectedOption ? selectedOption.label : intl.formatMessage({ id: 'IntegratedSearchBar.sizeLabel' });

                        return (
                          <select
                            {...input}
                            className={css.selectInput}
                            title={displayText}
                            data-tooltip={displayText}
                            onFocus={() => setActiveField('size')}
                            onBlur={() => setActiveField(null)}
                          >
                            <option value="">{intl.formatMessage({ id: 'IntegratedSearchBar.sizeLabel' })}</option>
                            {sizeOptions.slice(1).map(option => (
                              <option key={option.key} value={option.key}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        );
                      }}
                    />
                  )}
                </div>
                
                {/* Clear button for size */}
                {hasFieldSelection('size', isSizeMultiSelect, sizeOptions) && (
                  <button
                    type="button"
                    className={css.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearField('size');
                    }}
                  />
                )}
                
                {/* Size Multi-Select Filter (appears when size field is active and is multi-select) */}
                {activeField === 'size' && isSizeMultiSelect && (
                  <div 
                    ref={sizeDropdownRef}
                    className={css.brandFilterDropdown}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FieldCheckboxGroup
                      name="size"
                      id="IntegratedSearchBar.sizeFilter"
                      label={intl.formatMessage({ id: 'IntegratedSearchBar.sizeLabel' })}
                      options={sizeOptions}
                      twoColumns={false}
                    />
                  </div>
                )}
              </div>
              )}

              {/* Price Field - Hidden on mobile */}
              {!isMobile && (
              <Field
                name="price"
                render={({ input }) => {
                  const hasPrice = input.value && input.value !== '';
                  return (
              <div
                ref={priceDropdownRef}
                className={classNames(css.searchField, {
                  [css.active]: activeField === 'price',
                  [css.hasSelections]: hasPrice
                })}
                onClick={() => setActiveField('price')}
                style={{ position: 'relative' }}
              >
                <div className={classNames(css.fieldContent, {
                  [css.hasClearButton]: hasPrice
                })}>
                  <div className={classNames(css.fieldInput, {
                    [css.hasSelection]: hasPrice                 })}>
                    {hasPrice ? (
                      (() => {
                        const [min, max] = input.value.split(',');
                        return `$${min} - $${max}`;
                      })()
                    ) : (
                      <FormattedMessage id="IntegratedSearchBar.priceText" />
                    )}
                  </div>
                </div>

               {hasPrice && (
                  <button
                    type="button"
                    className={css.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      form.change('price', '');
                      
                    }}
                  />
                )}

                {activeField === 'price' && (
                  <div
                    className={css.brandFilterDropdown}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PriceFilter
                      id="IntegratedSearchBar.priceFilter"
                      queryParamNames={['price']}
                      initialValues={initialValues}
                      min={0}
                      max={350}
                      step={10}
                      marketplaceCurrency={marketplaceCurrency}
                      showAsPopup={false}
                      liveEdit={false}
                      fromSearchBar = {true}
                      onSubmit={(priceValues) => {
                        if (priceValues && priceValues.price) {
                          form.change('price', priceValues.price);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
                  );
                }}
              />
              )}

              {/* Search Button */}
              <div className={css.searchButtonContainer}>
                <Button
                  type="submit"
                  className={css.searchButton}
                >
                  <span className={css.searchButtonText}>
                    {isMobile?  <FormattedMessage id="IntegratedSearchBar.searchButtonMobile" />: <FormattedMessage id="IntegratedSearchBar.searchButton" />}
                  </span>
                </Button>
              </div>

            </div>

            {/* Sharetribe Price Filter (appears when price field is active) */}
            {/* {activeField === 'price' && (
              <div
                ref={priceDropdownRef}
                className={css.priceFilterDropdown}
                onClick={(e) => e.stopPropagation()}
              >
                <PriceFilter
                  id="IntegratedSearchBar.priceFilter"
                  queryParamNames={['price']}
                  initialValues={initialValues}
                  onChange={handlePriceFilterChange}
                  min={0}
                  max={350}
                  step={10}
                  marketplaceCurrency={marketplaceCurrency}
                  showAsPopup={false}
                  liveEdit={true}
                />
              </div>
            )} */}
          </Form>
          );
        }}
      />
    </div>
  );
};

export default IntegratedSearchBar;